/**
 * ESC-POS builder for Sunmi local receipt printing.
 * Ported from PHP EscPos.php + EscPosReceipts.php
 * Outputs raw strings for direct printing via getNative().print().
 */

import EscPos from "@/shared/helpers/EscPos";
import {useCurrencyStore} from "@/shared/stores";
import {getLocalDateFromServer} from "@/shared/helpers/dates";

// Printing of new orders' receipts (also failed ones), pre-bills and customer credit
export default class EscPosReceipts extends EscPos {

	static init() {
		if (Object.prototype.hasOwnProperty.call(EscPosReceipts, 'instance')) {
			return EscPosReceipts.instance;
		}
		EscPosReceipts.instance = new EscPosReceipts();
		return EscPosReceipts.instance;
	}
	hexToRawString(hex) {
		let raw = "";
		for (let i = 0; i < hex.length; i += 2) {
			raw += String.fromCharCode(parseInt(hex.substring(i, i + 2), 16));
		}
		return raw;
	}

	appendQRcode(text, moduleSize = 6, ecLevel = 3) {
		if (!text || text.length === 0) return "";
		let textLength = Math.min(text.length, 65535);
		moduleSize = Math.max(1, Math.min(16, moduleSize));
		ecLevel = Math.max(0, Math.min(3, ecLevel));

		let cmd = "1d286b040031410000";
		cmd += "1d286b03003143" + moduleSize.toString(16).padStart(2, "0");
		cmd += "1d286b03003145" + (ecLevel + 48).toString(16).padStart(2, "0");

		let pL = (textLength + 3) & 0xff;
		let pH = ((textLength + 3) >> 8) & 0xff;
		cmd += "1d286b" + pL.toString(16).padStart(2, "0") + pH.toString(16).padStart(2, "0") + "315030";

		for (let i = 0; i < textLength; i++) {
			cmd += text.charCodeAt(i).toString(16).padStart(2, "0");
		}
		cmd += "1d286b0300315130";
		return this.hexToRawString(cmd);
	}

	getVatValue(vat) {
		const map = {
			"4": 4, "5": 5, "10": 10, "22": 22, "2": 2,
			"6.4": 6.4, "7": 7, "7.3": 7.3, "7.5": 7.5,
			"7.65": 7.65, "7.95": 7.95, "8.3": 8.3, "8.5": 8.5,
			"8.8": 8.8, "9.5": 9.5, "12.3": 12.3
		};
		return map[String(vat)] ?? 0;
	}

	getPaymentName(paymentMethod) {
		const map = {
			"PC": "Pagamento contante",
			"PE": "Pagamento elettronico",
			"TR": "Pagamento ticket",
			"NR_CS": "Non riscosso",
			"NR_PS": "Non riscosso"
		};
		return map[paymentMethod] ?? "Altro";
	}

	formatNumber(num) {
		// Format number with 2 decimals, comma as decimal separator and dot as thousand separator
		let parts = num.toFixed(2).split(".");
		let intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
		return intPart + "," + parts[1];
	}

	getHeader(business) {
		let result = this.justifyCenter(this.replaceUnprintableChars(business.name || ""));
		let addressLine = (business.address || "") + (business.civic ? " " + business.civic : "");
		result += this.justifyCenter(this.replaceUnprintableChars(addressLine));
		result += this.justifyCenter(this.replaceUnprintableChars(
			(business.zip || "") + " " + (business.city || "") + (business?.province ? " (" + business.province + ")" : "")
		));
		result += this.justifyCenter(this.replaceUnprintableChars("P.IVA " + (business.vat || "")));
		result += "\n";
		return result;
	}

	customerCreditQr(customer, businessName, businessId) {
		let currencyStore = useCurrencyStore();
		let qrUrl = URL_HTML + "/c/?t=" + (customer.type === "company" ? "c" : "p") + customer.token + "&b=" + businessId;
		let credit = currencyStore.dbToActualValue(customer.credit || 0);
		let creditFormatted = this.formatNumber(credit) + " " + currencyStore.currCurrency.code;

		let now = new Date();
		let dateStr = (now.getHours() < 10 ? "0" : "") + now.getHours() + ":" +
			(now.getMinutes() < 10 ? "0" : "") + now.getMinutes() + " " +
			(now.getDate() < 10 ? "0" : "") + now.getDate() + "/" +
			((now.getMonth()+1) < 10 ? "0" : "") + (now.getMonth()+1);

		let result = this.spaceBetween("", dateStr, false, false);
		result += this.justifyCenter(this.bold(this.replaceUnprintableChars(customer.name || "Cliente")));
		result += "\n";
		if(businessName) {
			result += this.justifyCenter(this.bold(this.doubleSize(this.replaceUnprintableChars(businessName))));
			result += "\n";
		}
		result += "\n\n";
		result += this.justifyCenter("Credito disponibile:");
		result += "\n";
		result += this.justifyCenter(this.bold(this.doubleSize(creditFormatted)));
		result += "\n\n";
		if(qrUrl) {
			result += this.justifyCenter(this.appendQRcode(qrUrl, 6, 3));
			result += "\n\n";
			result += this.justifyCenter("Mostra il codice QR al personale per utilizzare il credito.");
		}
		result += "\n\n\n\n";
		result += "\x1d\x56\x31"; // partial cut

		return result;
	}

	/**
	 * SCONTRINO (FALLITO O FISCALE) + PRECONTO
	 * Build the full receipt ESC-POS raw string.
	 * @param {Object} business - { name, address, civic, zip, city, province, vat }
	 * @param {Object} receipt - { products, payments, adeId, dateCreated, paymentDiscount }
	 * @param {boolean} preBill
	 * @param {string[]} additionalLines
	 * @param {string|null} qrData - URL for QR code
	 * @param {boolean} adeError
	 * @returns {string} raw string for printing
	 */
	getPrintReceipt(business, receipt, preBill = false, additionalLines = [], qrData = null, adeError = false) {
		let result = this.getHeader(business);
		if (adeError) {
			result += this.justifyCenter("RICEVUTA NON FISCALE");
			result += this.justifyCenter("di vendita o prestazione");
		} else if (preBill) {
			result += this.justifyCenter("PRECONTO");
			result += this.justifyCenter("non valido ai fini fiscali");
		} else {
			result += this.justifyCenter("DOCUMENTO COMMERCIALE");
			result += this.justifyCenter("di vendita o prestazione");
		}

		result += "\n";
		result += this.fixedRight("DESCRIZIONE", "IVA      PREZZO", false);

		let total = 0;
		let vatTotal = 0;
		for (const item of (receipt.products || [])) {
			let vatValue = this.getVatValue(item.vat);
			let vatExtraSpace = vatValue < 10 ? " " : "";
			let vatFormatted = " " + vatExtraSpace + this.formatNumber(vatValue);
			let priceFormatted = this.formatNumber((item.quantity * item.price) / 100);
			let extraSpaces = 16 - vatFormatted.length - priceFormatted.length;
			extraSpaces = extraSpaces > 0 ? " ".repeat(extraSpaces) : "";
			result += this.fixedRight(
				item.quantity + "x " + this.replaceUnprintableChars(item.name || ""),
				vatFormatted + "% " + extraSpaces + priceFormatted,
				false
			);

			let productTotal = item.price * item.quantity;
			total += productTotal;
			vatTotal += productTotal - (productTotal / (1 + (vatValue / 100)));

			if (item.discount && item.discount > 0) {
				let discountFormatted = this.formatNumber(item.discount / 100);
				let discountExtraSpaces = 16 - vatFormatted.length - discountFormatted.length - 1;
				discountExtraSpaces = discountExtraSpaces > 0 ? " ".repeat(discountExtraSpaces) : "";
				result += this.fixedRight(
					"  Sconto",
					vatFormatted + "% " + discountExtraSpaces + "-" + discountFormatted,
					false
				);
				total -= item.discount;
				let discountVatAmount = item.discount - (item.discount / (1 + (vatValue / 100)));
				vatTotal -= discountVatAmount;
			}
		}

		let paymentDiscount = receipt.paymentDiscount || 0;
		if (paymentDiscount) {
			result += this.spaceBetween("SUBTOTALE", this.formatNumber(total / 100), false, true);
			result += this.spaceBetween("Sconto", "-" + this.formatNumber(paymentDiscount / 100), false, true);
			let vatDiscount = (vatTotal / total) * paymentDiscount;
			total -= paymentDiscount;
			vatTotal -= vatDiscount;
		}

		result += "\n";
		result += this.spaceBetween("TOTALE", this.formatNumber(total / 100), true, true);
		let doubleHeightVat = this.columns() > 32; // if paper is not wide enough, print small vat to save space
		result += this.spaceBetween("di cui IVA", this.formatNumber(vatTotal / 100), doubleHeightVat, true);
		if (!preBill) {
			result += "\n";
		}
		result += "\x1b\x21\x00"; // reset styles

		if (!preBill) {
			let paidTotal = 0;
			for (const payment of (receipt.payments || [])) {
				let paymentName = this.getPaymentName(payment.paymentMethod);
				result += this.spaceBetween(paymentName, this.formatNumber(payment.amount / 100));
				if (payment.paymentMethod !== "NR_CS" && payment.paymentMethod !== "NR_PS") {
					paidTotal += payment.amount;
				}
			}
			if (paymentDiscount) {
				result += this.spaceBetween("Sconto a pagare", this.formatNumber(paymentDiscount / 100));
			}
			result += this.spaceBetween("Importo pagato", this.formatNumber(paidTotal / 100));
			result += "\n";

			let dateCreated = receipt.dateCreated || "";
			try {
				if (dateCreated) {
					let d = getLocalDateFromServer(dateCreated);
					if (!isNaN(d.getTime())) {
						dateCreated = d.toLocaleString("it-IT", {
							day: "2-digit", month: "2-digit", year: "numeric",
							hour: "2-digit", minute: "2-digit", second: "2-digit"
						});
					}
				}
			} catch (e) {
				// keep original
			}
			result += this.justifyCenter(this.replaceUnprintableChars(dateCreated));
			if(!adeError) {
				result += this.justifyCenter("DOCUMENTO N. " + (receipt.adeId || ""));
			}
			result += "\n";
			for (const line of additionalLines) {
				let parts = (this.columns() <= 32 && line.includes(" - Pagamento")) ? line.split(" - ") : [line];
				for (const part of parts) {
					result += this.bold(this.doubleSize(this.replaceUnprintableChars(part))) + "\n";
				}
			}
			if (!adeError) {
				let additionalSpaces = this.columns() === 32 ? '  ' : '';
				result += "Generato elettronicamente con" + additionalSpaces + " apiscontrino.com\n";
			} else {
				result += "\n";
				result += this.justifyCenter("AdE NON DISPONIBILE.");
				result += "\n";
				result += "Appena il sistema dell'Agenzia delle Entrate tornera in funzione, sara possibile emettere il documento fiscale.";
			}
		} else {
			result += "\n";
			for (const line of additionalLines) {
				result += this.replaceUnprintableChars(line) + "\n";
			}
		}

		if (qrData && !adeError) {
			result += "\n";
			result += this.justifyCenter(this.appendQRcode(qrData, 6, 1));
		}

		result += "\n\n\n\n\n\n";
		result += "\x1d\x56\x31"; // cut paper (partial cut)

		return result;
	}
}
